import { CommonModule } from '@angular/common';
import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import type {
  MeetingRequest,
  MeetingResponse,
} from '@app/models/meeting.model';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { ValidatedDateTimePickerComponent } from '@app/ui-components/validated-datetime-picker/validated-datetime-picker.component';
import { ValidatedTextEditorComponent } from '@app/ui-components/validated-text-editor/validated-text-editor.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-schedule-meeting',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    ButtonComponent,
    ValidatedTextEditorComponent,
    ValidatedDateTimePickerComponent,
  ],
  templateUrl: './schedule-meeting.component.html',
  styleUrl: './schedule-meeting.component.scss',
})
export class ScheduleMeetingComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(ProjectAdminService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  projectId = input.required<string>();
  meeting = input<MeetingResponse | null>(null);
  created = output<void>();
  btnDisabled = input<boolean>(false);
  hasContact = input<boolean>(false);
  loadingMeetings = input<boolean>(false);
  open = signal(false);
  sending = signal(false);

  readonly isDisabled = computed(
    () => this.loadingMeetings() || !this.hasContact() || this.sending()
  );

  get readOnly(): boolean {
    return (this.meeting()?.status ?? 0) > 1;
  }

  get acceptedIndex(): number {
    const meeting = this.meeting();
    if (!meeting || !meeting.acceptedDate) return -1;
    const acc = this.normalizeForInput(meeting.acceptedDate);
    const opts = [
      meeting.offeredDate1,
      meeting.offeredDate2,
      meeting.offeredDate3,
    ].map((value) => (value ? this.normalizeForInput(value) : null));
    return opts.findIndex((value) => value === acc);
  }

  form = this.fb.group({
    option1: this.fb.control<string | null>(null, {
      validators: [Validators.required],
    }),
    option2: this.fb.control<string | null>(null, {
      validators: [Validators.required],
    }),
    option3: this.fb.control<string | null>(null, {
      validators: [Validators.required],
    }),
    messageHtml: this.fb.control<string>('', {
      validators: [Validators.required],
    }),
  });

  ngOnInit(): void {
    this.applyMeetingToForm();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if ('meeting' in changes) this.applyMeetingToForm();
    if ('loadingMeetings' in changes || 'hasContact' in changes) {
      this.updateFormLock();
    }
  }

  private updateFormLock(): void {
    if (this.isDisabled() || this.readOnly) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  openDialog(): void {
    if (this.isDisabled()) return;
    this.open.set(true);
  }
  closeDialog(force = false): void {
    if (!force && this.sending()) return;
    this.open.set(false);
  }
  stopClose(event: MouseEvent): void {
    event.stopPropagation();
  }

  private normalizeForInput(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    const pad = (number: number) => String(number).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
  private toIso(value: string | null): string | null {
    return value ? new Date(value).toISOString() : null;
  }

  private applyMeetingToForm(): void {
    const meeting = this.meeting();
    if (!meeting) {
      this.form.enable({ emitEvent: false });
      this.form.reset(
        { option1: null, option2: null, option3: null, messageHtml: '' },
        { emitEvent: false }
      );
      return;
    }
    this.form.patchValue(
      {
        option1: meeting.offeredDate1
          ? this.normalizeForInput(meeting.offeredDate1)
          : null,
        option2: meeting.offeredDate2
          ? this.normalizeForInput(meeting.offeredDate2)
          : null,
        option3: meeting.offeredDate3
          ? this.normalizeForInput(meeting.offeredDate3)
          : null,
        messageHtml: meeting.message ?? '',
      },
      { emitEvent: false }
    );

    this.readOnly
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  readonly hasAccepted = computed<boolean>(
    () => !!this.meeting()?.acceptedDate
  );

  submit(): void {
    if (this.readOnly) return;
    if (this.isDisabled()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { option1, option2, option3, messageHtml } = this.form.getRawValue();
    const body: MeetingRequest = {
      projectId: this.projectId(),
      offeredDate1: this.toIso(option1),
      offeredDate2: this.toIso(option2),
      offeredDate3: this.toIso(option3),
      message: messageHtml ?? null,
    };

    this.sending.set(true);
    this.service
      .createMeeting(body)
      .pipe(finalize(() => this.sending.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.scheduleMeeting.toasts.sent')
          );
          this.created.emit();
          this.closeDialog(true);
        },
        error: () => {
          this.toast.error(
            this.translate.instant('admin.scheduleMeeting.toasts.failed')
          );
        },
      });
  }
}
